import { Box, Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function Pagination({ currentPage, totalPages, onPageChange }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const getPageNumbers = () => {
        const pages = [];
        const showPages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
        let endPage = Math.min(totalPages, startPage + showPages - 1);

        if (endPage - startPage < showPages - 1) {
            startPage = Math.max(1, endPage - showPages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    const pages = getPageNumbers();

    const baseBtn = {
        minWidth: 40,
        height: 40,
        px: 1.5,
        borderRadius: '6px',
        borderColor: '#d1d5db',
        color: '#374151',
        textTransform: 'none',
        fontWeight: 500,
        '&:hover': { bgcolor: '#f9fafb' },
    };

    return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1} flexWrap="nowrap">
            {/* PREV */}
            <Button
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                sx={baseBtn}
                aria-label="Go to previous page"
            >
                <ChevronLeft size={18} />
            </Button>

            {/* MOBILE VIEW */}
            {isMobile ? (
                <Button
                    variant="outlined"
                    sx={{
                        ...baseBtn,
                        bgcolor: '#047857',
                        color: '#fff',
                        borderColor: '#047857',
                        minWidth: 80,
                        '&:hover': { bgcolor: '#065f46' },
                    }}
                >
                    {currentPage} / {totalPages}
                </Button>
            ) : (
                <>
                    {/* FIRST */}
                    {pages[0] > 1 && (
                        <>
                            <Button
                                variant="outlined"
                                onClick={() => onPageChange(1)}
                                sx={baseBtn}
                            >
                                1
                            </Button>
                            {pages[0] > 2 && (
                                <Box sx={{ px: 1, color: '#9ca3af' }}>…</Box>
                            )}
                        </>
                    )}

                    {/* NUMBERS */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            onClick={() => onPageChange(page)}
                            variant="outlined"
                            sx={{
                                ...baseBtn,
                                bgcolor: currentPage === page ? '#047857' : 'transparent',
                                borderColor: currentPage === page ? '#047857' : '#d1d5db',
                                color: currentPage === page ? '#fff' : '#374151',
                                '&:hover': {
                                    bgcolor: currentPage === page ? '#065f46' : '#f9fafb',
                                },
                            }}
                        >
                            {page}
                        </Button>
                    ))}

                    {/* LAST */}
                    {pages[pages.length - 1] < totalPages && (
                        <>
                            {pages[pages.length - 1] < totalPages - 1 && (
                                <Box sx={{ px: 1, color: '#9ca3af' }}>…</Box>
                            )}
                            <Button
                                variant="outlined"
                                onClick={() => onPageChange(totalPages)}
                                sx={baseBtn}
                            >
                                {totalPages}
                            </Button>
                        </>
                    )}
                </>
            )}

            {/* NEXT */}
            <Button
                variant="outlined"
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                sx={baseBtn}
                aria-label="Go to next page"
            >
                <ChevronRight size={18} />
            </Button>
        </Box>
    );
}
